import { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const ConfigContext = createContext();

export const useConfig = () => {
    const context = useContext(ConfigContext);
    if (!context) {
        throw new Error('useConfig must be used within a ConfigProvider');
    }
    return context;
};

// Helper: safely parse JSON strings or return objects as-is
const parseConfigField = (field) => {
    if (!field) return field;
    return typeof field === 'string' ? JSON.parse(field) : field;
};

// Helper: safely stringify before sending to API
const stringifyConfigField = (field) => JSON.stringify(field);

export const ConfigProvider = ({ children }) => {
    const [config, setConfig] = useState({
        platformName: 'Islam 101',
        platformLogo: '/logo.svg',
        donationLink: '',
        reciters: [],
        contactEmail: '',
        supportPhone: '',
        address: '',
        socialLinks: {
            facebook: '',
            twitter: '',
            instagram: '',
            youtube: ''
        }
    });

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const { data } = await api.get('/config');

                // Parse fields safely
                const platformSettings = parseConfigField(data.platform_settings) || {};
                const reciters = parseConfigField(data.reciters) || [];

                setConfig(prev => ({
                    ...prev,
                    ...platformSettings,
                    reciters: reciters.length > 0 ? reciters : prev.reciters
                }));
            } catch (error) {
                console.error('Failed to load config', error);
            }
        };

        fetchConfig();
    }, []);

    // Update platform settings
    const updateConfig = async (updates) => {
        // Optimistic update
        setConfig(prev => ({ ...prev, ...updates }));

        try {
            const settings = {
                platformName: updates.platformName ?? config.platformName,
                platformLogo: updates.platformLogo ?? config.platformLogo,
                donationLink: updates.donationLink ?? config.donationLink,
                contactEmail: updates.contactEmail ?? config.contactEmail,
                supportPhone: updates.supportPhone ?? config.supportPhone,
                address: updates.address ?? config.address,
                socialLinks: updates.socialLinks ?? config.socialLinks,
                security: updates.security ?? config.security
            };

            await api.post('/config', {
                key: 'platform_settings',
                value: stringifyConfigField(settings) // always stringify
            });
        } catch (error) {
            console.error('Failed to save config', error);
        }
    };

    // Reciter helpers
    const addReciter = async (reciter) => {
        const newReciters = [...config.reciters, reciter];
        setConfig(prev => ({ ...prev, reciters: newReciters }));

        try {
            await api.post('/config', {
                key: 'reciters',
                value: stringifyConfigField(newReciters)
            });
        } catch (err) { console.error(err); }
    };

    const removeReciter = async (reciterId) => {
        const newReciters = config.reciters.filter(r => r.id !== reciterId);
        setConfig(prev => ({ ...prev, reciters: newReciters }));

        try {
            await api.post('/config', {
                key: 'reciters',
                value: stringifyConfigField(newReciters)
            });
        } catch (err) { console.error(err); }
    };

    const updateReciter = async (id, updatedData) => {
        const newReciters = config.reciters.map(r =>
            r.id === id ? { ...r, ...updatedData } : r
        );
        setConfig(prev => ({ ...prev, reciters: newReciters }));

        try {
            await api.post('/config', {
                key: 'reciters',
                value: stringifyConfigField(newReciters)
            });
        } catch (err) { console.error(err); }
    };

    const value = {
        config,
        updateConfig,
        addReciter,
        removeReciter,
        updateReciter
    };

    return (
        <ConfigContext.Provider value={value}>
            {children}
        </ConfigContext.Provider>
    );
};
